import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Zap, Calendar, Star, Users } from 'lucide-react';

const PlugRelationshipModal = ({ isOpen, onClose, friend, onSubmit }) => {
  const [plugData, setPlugData] = useState({
    is_new_plug: true,
    relationship_start_date: '',
    times_used: 1,
    service_context: ''
  });

  const handleInputChange = (field, value) => {
    setPlugData(prev => ({
      ...prev,
      [field]: field === 'times_used' ? parseInt(value) || 1 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If it's a new plug, set start date to today
    const finalData = {
      ...plugData,
      relationship_start_date: plugData.is_new_plug 
        ? new Date().toISOString().split('T')[0]
        : plugData.relationship_start_date
    };
    
    await onSubmit(finalData);
    
    // Reset form
    setPlugData({
      is_new_plug: true,
      relationship_start_date: '',
      times_used: 1,
      service_context: ''
    });
    
    onClose();
  };

  const handleClose = () => {
    setPlugData({
      is_new_plug: true,
      relationship_start_date: '',
      times_used: 1,
      service_context: ''
    });
    onClose();
  };

  if (!friend) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" style={{color: 'var(--brand-tan)'}} />
            Your Relationship with {friend.name}
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-2">
            Help us understand your professional relationship to provide better recommendations.
          </p>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* New vs Existing Plug */}
          <div>
            <Label>Is this a new professional connection?</Label>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="plug_type"
                  checked={plugData.is_new_plug}
                  onChange={() => handleInputChange('is_new_plug', true)}
                  className="text-blue-600"
                />
                <span className="text-sm">Yes, brand new connection</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="plug_type"
                  checked={!plugData.is_new_plug}
                  onChange={() => handleInputChange('is_new_plug', false)}
                  className="text-blue-600"
                />
                <span className="text-sm">No, I've worked with them before</span>
              </label>
            </div>
          </div>

          {/* If existing relationship, ask for details */}
          {!plugData.is_new_plug && (
            <>
              <div>
                <Label htmlFor="startDate" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  When did you first start working with them?
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={plugData.relationship_start_date}
                  onChange={(e) => handleInputChange('relationship_start_date', e.target.value)}
                  className="mt-1"
                  max={new Date().toISOString().split('T')[0]}
                  required={!plugData.is_new_plug}
                />
              </div>

              <div>
                <Label htmlFor="timesUsed" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Approximately how many times have you used their services?
                </Label>
                <Input
                  id="timesUsed"
                  type="number"
                  min="1"
                  max="100"
                  value={plugData.times_used}
                  onChange={(e) => handleInputChange('times_used', e.target.value)}
                  className="mt-1"
                  placeholder="e.g., 5"
                />
              </div>
            </>
          )}

          {/* Service Context */}
          <div>
            <Label htmlFor="serviceContext" className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              What type of work have they done for you? (Optional)
            </Label>
            <Textarea
              id="serviceContext"
              value={plugData.service_context}
              onChange={(e) => handleInputChange('service_context', e.target.value)}
              className="mt-1"
              rows={2}
              placeholder="e.g., Fixed my AC, home renovation, car repairs..."
            />
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-blue-800 text-sm">
                Why do we ask this?
              </span>
            </div>
            <p className="text-xs text-blue-600">
              Understanding your relationship helps us provide better recommendations to others 
              and shows the strength of your professional network. This information builds trust 
              and credibility for both you and {friend.name}.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              className="flex-1"
            >
              Skip for Now
            </Button>
            <Button 
              type="submit" 
              className="flex-1 text-white"
              style={{
                background: 'linear-gradient(135deg, var(--brand-tan) 0%, var(--brand-tan-dark) 100%)'
              }}
            >
              Add to Network
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PlugRelationshipModal;